import { ApiError } from '../api';

// Base error types that can be processed by the error system
export type ProcessableError = Error | ApiError | string | unknown;

// Error boundary props with proper typing
export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  context?: string;
  level?: 'page' | 'component' | 'widget';
}

// Error fallback component props
export interface ErrorFallbackProps {
  error?: ProcessedError;
  onRetry?: () => void;
  onGoBack?: () => void;
  onGoHome?: () => void;
  className?: string;
}

// Compact error fallback props
export interface CompactErrorFallbackProps extends ErrorFallbackProps {
  title?: string;
  showDetails?: boolean;
}

// Error context for tracking where errors occur
export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
}

// Processed error with all metadata
export interface ProcessedError {
  id: string;
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  originalError: ProcessableError;
  context?: ErrorContext;
  timestamp: string;
  shouldRetry: boolean;
  shouldReport: boolean;
  userMessage: string;
  technicalMessage: string;
  suggestions: string[];
}

// Error report for external services
export interface ErrorReport {
  id: string;
  timestamp: string;
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  stack?: string;
  context?: ErrorContext;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId?: string;
}

// Error log entry for client-side logging
export interface ErrorLog {
  id: string;
  timestamp: string;
  error: {
    name: string;
    message: string;
    stack?: string;
  };
  errorInfo: {
    componentStack: string | null;
  };
  context?: string;
  userAgent: string;
  url: string;
  userId?: string;
}

// Error types for categorization
export enum ErrorType {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  SERVER = 'server',
  CLIENT = 'client',
  UNKNOWN = 'unknown',
}

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

// Error handler options
export interface ErrorHandlerOptions {
  showToast?: boolean;
  toastDuration?: number;
  logToConsole?: boolean;
}

// Form error handler options
export interface FormErrorHandlerOptions {
  showToast?: boolean;
  duration?: number;
}

// Query error handler options
export interface QueryErrorHandlerOptions {
  showToast?: boolean;
  duration?: number;
}

// Error service configuration
export interface ErrorServiceConfig {
  maxQueueSize?: number;
  reportingEnabled?: boolean;
  developmentMode?: boolean;
}

// Error notification options
export interface ErrorNotificationOptions {
  duration?: number;
  showSuggestions?: boolean;
}

// Error recovery options
export interface ErrorRecoveryOptions {
  maxRetries?: number;
  retryDelay?: number;
  exponentialBackoff?: boolean;
}

// Type guards for error checking
export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
};

export const isError = (error: unknown): error is Error => {
  return error instanceof Error;
};

export const isString = (error: unknown): error is string => {
  return typeof error === 'string';
};

export const isProcessedError = (error: unknown): error is ProcessedError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'id' in error &&
    'type' in error &&
    'severity' in error &&
    'message' in error
  );
};

// Utility types for error handling hooks
export type ErrorHandler = (
  error: ProcessableError,
  context?: string | ErrorContext,
  options?: ErrorHandlerOptions
) => ProcessedError;

export type FormErrorHandler = (
  error: ProcessableError,
  formName?: string,
  setError?: (field: string, error: { message: string }) => void,
  options?: FormErrorHandlerOptions
) => ProcessedError;

export type QueryErrorHandler = (
  error: ProcessableError,
  queryKey?: string[],
  options?: QueryErrorHandlerOptions
) => ProcessedError;

export type MutationErrorHandler = (
  error: ProcessableError,
  mutationKey?: string,
  options?: QueryErrorHandlerOptions
) => ProcessedError;

export type ComponentErrorHandler = (
  error: ProcessableError,
  action?: string,
  options?: ErrorHandlerOptions
) => ProcessedError;

// Error boundary state
export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorId?: string;
}

// Error reporter state
export interface ErrorReporterState {
  errors: ProcessedError[];
  isOpen: boolean;
  selectedError: ProcessedError | null;
}